/**
 * Created: 2026-02-20
 * Initial commit: Add system test page with error logging mutation
 *
 * Dev-only page for triggering system test mutations.
 */

import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { Box, Button, Stack, Typography } from '@mui/material'
import {
  TestLogErrorDocument,
  TestRaiseExceptionDocument,
  TestSendMessageDocument,
} from '../gql/graphql'

gql`
  mutation TestLogError {
    testLogError {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }

  mutation TestSendMessage {
    testSendMessage {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }

  mutation TestRaiseException {
    testRaiseException {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

function triggerMalformedQuery() {
  const token = localStorage.getItem('token')
  fetch('/graphql', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ query: '{ sites { nonExistentField } }' }),
  })
}

export default function SystemTestPage() {
  const [testLogError, { loading: loadingLog }] = useMutation(TestLogErrorDocument)
  const [testSendMessage, { loading: loadingMessage }] = useMutation(
    TestSendMessageDocument,
  )
  const [testRaiseException, { loading: loadingException }] = useMutation(
    TestRaiseExceptionDocument,
  )

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        System Test
      </Typography>
      <Stack direction="column" spacing={3} sx={{ maxWidth: 600 }}>
        <Box>
          <Button
            variant="outlined"
            color="warning"
            disabled={loadingLog}
            onClick={() => testLogError()}
            fullWidth
          >
            Trigger Error Log
          </Button>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            Purpose: Test structlog error logging with request context.
            <br />
            Expected: A mock error log entry with client_ip, openid, and other context
            fields.
          </Typography>
        </Box>

        <Box>
          <Button
            variant="outlined"
            color="error"
            onClick={triggerMalformedQuery}
            fullWidth
          >
            Trigger GraphQL Validation Error
          </Button>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            Purpose: Test GraphQL validation error logging via
            StructlogErrorExtension.on_validate.
            <br />
            Expected: Validation error re-logged with request context (client_ip,
            openid, etc.).
          </Typography>
        </Box>

        <Box>
          <Button
            variant="outlined"
            color="error"
            disabled={loadingException}
            onClick={() => testRaiseException()}
            fullWidth
          >
            Trigger Unhandled Exception
          </Button>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            Purpose: Test GraphQL execution error logging via
            StructlogErrorExtension.on_execute.
            <br />
            Expected: RuntimeError re-logged with request context (client_ip, openid,
            etc.).
          </Typography>
        </Box>

        <Box>
          <Button
            variant="outlined"
            disabled={loadingMessage}
            onClick={() => testSendMessage()}
            fullWidth
          >
            Send WXWork Message
          </Button>
          <Typography
            variant="caption"
            display="block"
            sx={{ mt: 1, color: 'text.secondary' }}
          >
            Purpose: Test messenger-service integration (WXWork message sending).
            <br />
            Expected: A test message sent via WXWork to the configured account.
          </Typography>
        </Box>
      </Stack>
    </Box>
  )
}
